import React, { useState } from "react";
import { applyMoodLayers } from "../controllers/lightsParameters";
import { FaEdit, FaPlay, FaSave, FaTrash, FaPlus } from "react-icons/fa";

const STORAGE_KEY = "lights_moods_v1";

function loadMoods() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // If already an array: validate shape minimally
    if (Array.isArray(parsed)) {
      return parsed.filter((e) => e && typeof e === 'object' && 'name' in e && 'layers' in e);
    }
    // Legacy object shape: { name: layers, ... } → [{name, layers}]
    if (parsed && typeof parsed === 'object') {
      const arr = [];
      for (const key of Object.keys(parsed)) {
        arr.push({ name: key, layers: parsed[key] });
      }
      // Persist migration
      saveMoods(arr);
      return arr;
    }
    return [];
  } catch (_) {
    return [];
  }
}

function saveMoods(moodsArray) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(moodsArray));
  } catch (_) {
    // ignore
  }
}

export default function LightsMoods({ layers }) {
  const cloneLayers = (src) => {
    try {
      return JSON.parse(JSON.stringify(src));
    } catch (_) {
      return Array.isArray(src) ? src.map((l) => ({ ...l, color: Array.isArray(l?.color) ? [...l.color] : l?.color })) : src;
    }
  };
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const applyingRef = React.useRef(false);
  const [moods, setMoods] = useState(() => loadMoods());

  // For array-based moods, rendering directly from moods preserves order

  const applyMoodAt = async (index) => {
    const entry = moods[index];
    if (!entry) return;
    if(applyingRef.current)return;
    applyingRef.current=true;setApplying(true);setApplyError(null);
    try {await applyMoodLayers(entry.layers);}
    catch(e){setApplyError(`Mood stopped: ${e.message}. Some layers may already have changed.`);}
    finally{applyingRef.current=false;setApplying(false);}
  };

  const saveOverwriteAt = (index) => {
    const entry = moods[index];
    if (!entry) return;
    if (!Array.isArray(layers) || layers.length === 0) return;
    if (!window.confirm(`Overwrite mood "${entry.name}" with current layers?`)) return;
    const next = moods.slice();
    next[index] = { ...entry, layers: cloneLayers(layers) };
    setMoods(next);
    saveMoods(next);
  };

  const renameMoodAt = (index) => {
    const entry = moods[index];
    if (!entry) return;
    const to = prompt(`Rename mood "${entry.name}" to:`, entry.name);
    const target = (to || "").trim();
    if (!target || target === entry.name) return;
    const next = moods.slice();
    next[index] = { ...entry, name: target };
    setMoods(next);
    saveMoods(next);
  };

  const deleteMoodAt = (index) => {
    const entry = moods[index];
    if (!entry) return;
    if (!window.confirm(`Delete mood "${entry.name}"?`)) return;
    const next = moods.slice();
    next.splice(index, 1);
    setMoods(next);
    saveMoods(next);
  };

  const saveNew = () => {
    // Create default name: Mood X (not guaranteed unique, but fine for duplicates)
    let i = moods.length + 1;
    const name = `Mood ${i}`;
    const payload = Array.isArray(layers) ? cloneLayers(layers) : [];
    const next = moods.concat({ name, layers: payload });
    setMoods(next);
    saveMoods(next);
  };

  return (
    <div className="panel-box">
      <h3 style={{ marginTop: 0, textAlign: "left", marginBottom: 8 }}>Moods</h3>
      {applyError && <p role="alert">{applyError}</p>}
      <div>
        {moods.length === 0 && (
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>No saved moods yet.</div>
        )}
        {moods.map((entry, idx) => (
          <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #333" }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button disabled={applying} aria-label="Load" title="Load/Apply" onClick={() => applyMoodAt(idx)}><FaPlay /></button>
              <div style={{ fontWeight: 600 }}>{entry.name}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button aria-label="Rename" title="Rename" onClick={() => renameMoodAt(idx)}><FaEdit /></button>
              <button aria-label="Save" title="Save (overwrite)" onClick={() => saveOverwriteAt(idx)}><FaSave /></button>
              <button aria-label="Delete" title="Delete" onClick={() => deleteMoodAt(idx)}><FaTrash /></button>
            </div>
          </div>
        ))}
        {/* Add new mood row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "10px 0" }}>
          <button aria-label="Add Mood" title="Add Mood" onClick={saveNew}><FaPlus /> Add Mood</button>
        </div>
      </div>
    </div>
  );
}
